import { Menu, MenuButton, MenuItem, MenuItems } from '@headlessui/react';
import classNames from 'classnames';
import type { ReactNode } from 'react';
import { Link } from 'react-router';
import { HlxLogo, McvLogo } from '~/components/logos';
import { BRAND_CONFIGS, type SiteBrand } from '~/config/branding';
import { buildBrandSwitchUrl, OTHER_BRAND } from '~/routing/brandSwitch';

// `aria-hidden` because the logo sits INSIDE a control that already names the
// brand in its text ("Switch to HealthcareLX"). Left exposed, the logo's own
// alt text is concatenated into the accessible name and screen readers announce
// the brand twice.
const BrandIcon = ({ brand, height = 16 }: { brand: SiteBrand; height?: number }) => (
  <span aria-hidden="true" className="flex items-center">
    {brand === 'hlx' ? (
      <HlxLogo label="icon" height={height} />
    ) : (
      <McvLogo label="icon" height={height} />
    )}
  </span>
);

/**
 * Real document navigation, so it must be a real anchor.
 *
 * It cannot be a react-router `<Link>` — the target is cross-origin, and a
 * client-side route transition would never leave this app. But a NATIVE
 * `<a href>` is exactly right, and is what these controls use: a button that
 * imperatively called `window.location.assign` announced itself to assistive
 * technology as an action rather than a link, and silently dropped
 * middle-click, open-in-new-tab, copy-link-address and the browser's status
 * bar preview. Clerk session continuity comes from the sync query parameter
 * baked into the URL, which an href carries just as well as an assign() call.
 *
 * The href is computed during render rather than on click. It depends on
 * `window.location.hostname`, which cannot change without a navigation that
 * would remount this anyway. [MCV-1513]
 *
 * [MCV-1719] It also depends on the Clerk session cookie, which CAN change
 * under a mounted header (sign-out in another tab). `BrandSwitchAction` only
 * mounts while the menu is open, so that read happens at open time.
 */
const getBrandSwitchHref = (
  targetBrand: SiteBrand,
  targetPath: string | undefined,
  surface: BrandMenuSurface
) =>
  // [MCV-1719] The public header is the only surface a signed-out visitor can
  // reach, and a signed-out switch must not ask the satellite to sync.
  buildBrandSwitchUrl(targetBrand, targetPath, { sessionAware: SIGNED_OUT_SURFACES.has(surface) });

/**
 * Which mount site this instance is. Required, and it suffixes the data-cy
 * attributes, because several instances are alive in the DOM AT THE SAME TIME:
 * NavHeader/MarketHeader keep theirs mounted (only CSS-hidden) while `Layout`
 * separately mounts `MobileMenu`'s copy, and `PublicHeader` renders one in its
 * always-on nav plus another inside its open drawer. A single shared data-cy
 * would therefore match 2 elements and break the very selectors it exists to
 * stabilise.
 */
export type BrandMenuSurface =
  | 'nav-header'
  | 'market-header'
  | 'public-header'
  | 'public-header-drawer'
  | 'mobile-menu';

const SIGNED_OUT_SURFACES: ReadonlySet<BrandMenuSurface> = new Set([
  'public-header',
  'public-header-drawer',
]);

const BrandSwitchAction = ({
  otherBrand,
  targetPath,
  surface,
}: {
  otherBrand: SiteBrand;
  targetPath?: string;
  surface: BrandMenuSurface;
}) => (
  <>
    <div className="my-1.5 mx-3 border-t border-grey-900" />
    <MenuItem>
      <a
        href={getBrandSwitchHref(otherBrand, targetPath, surface)}
        data-cy={`brand-switch-action-${surface}`}
        className="flex w-[calc(100%-12px)] items-center gap-2 mx-1.5 px-2.5 py-1.5 rounded-md text-[13px] text-grey-200 data-[focus]:bg-grey-900/50"
      >
        <BrandIcon brand={otherBrand} height={16} />
        Switch to {BRAND_CONFIGS[otherBrand].siteName}
      </a>
    </MenuItem>
  </>
);

interface HeaderBrandMenuProps {
  /** The brand this header currently represents. */
  brand: SiteBrand;
  /** Distinguishes co-mounted instances — see `BrandMenuSurface`. */
  surface: BrandMenuSurface;
  /** The existing logo element the trigger wraps. */
  children: ReactNode;
  /** Where the logo used to link before it became a menu trigger. */
  primaryPath: string;
  /** Label for the menu item that preserves the old logo-click destination. */
  primaryLabel: string;
  /** Any side effect the old logo `<Link>`'s onClick had (e.g. NavHeader's setActivePage). */
  onPrimaryClick?: () => void;
  triggerAriaLabel: string;
  triggerClassName?: string;
  /** Optional role-aware destination on the other brand. */
  switchTargetPath?: string;
  allowBrandSwitch?: boolean;
  /**
   * Render the dropdown in place instead of portalled to `document.body`.
   * Required inside `MobileMenu`: that drawer runs its own focus trap scoped to
   * `menuRef.current`, and headlessui's `anchor` prop portals the panel out to
   * a sibling of the render root — so its items fall outside the trap's
   * `querySelectorAll` and Tab escapes the open modal dialog.
   */
  inline?: boolean;
}

/**
 * Dropdown-on-the-logo brand switcher — MCV-1513's primary mechanism for
 * showing MCV and HLX are related and letting a user move between them. Wraps
 * an existing header logo so both brands are visible together: the current
 * brand heads the menu (marked "current"), the other brand is offered as an
 * explicit "Switch to <name>" action. Positioning is handled entirely by
 * headlessui's `anchor` prop (floating-ui backed, portalled) rather than
 * hand-rolled — this is the first use of headlessui `Menu` in this app.
 *
 * Design choice: the logo itself becomes the menu trigger instead of staying
 * a plain link. `MenuButton` owns its own click handler to open/close the
 * menu, which would swallow a navigating `onClick`/`href` if the trigger were
 * rendered `as` a `Link`. Nothing is lost, though — the logo's original
 * destination is the menu's first item (`primaryLabel`), reachable in one
 * more click, with `onPrimaryClick` preserving whatever side effect the old
 * `<Link>` had.
 */
export const HeaderBrandMenu = ({
  brand,
  surface,
  children,
  primaryPath,
  primaryLabel,
  onPrimaryClick,
  triggerAriaLabel,
  triggerClassName,
  switchTargetPath,
  inline = false,
  allowBrandSwitch = true,
}: HeaderBrandMenuProps) => {
  const otherBrand = OTHER_BRAND[brand];
  const currentConfig = BRAND_CONFIGS[brand];

  return (
    <Menu as="div" className="relative inline-block text-left">
      <MenuButton
        // Elsewhere in this app an aria-label doubled as a click-away hook +
        // e2e selector and became load-bearing on its exact text (the
        // notification bell). Key tests/selectors off this stable data-cy
        // instead, so the label copy stays free to change.
        data-cy={`brand-switch-menu-trigger-${surface}`}
        aria-label={triggerAriaLabel}
        className={triggerClassName ?? 'block'}
      >
        {children}
      </MenuButton>
      <MenuItems
        // [MCV-1563] `modal` defaults to true, which writes an inline
        // `overflow: hidden` on <html> while the panel is open. That beats the
        // `public-shell-scroll` gutter reservation (inline > stylesheet), and
        // headlessui's compensating `padding-right` cannot reach a
        // `position: fixed` header — so the centered header slides by the
        // scrollbar width every time the logo menu opens.
        modal={false}
        // `anchor` is what makes headlessui portal the panel to document.body,
        // so it must be omitted (and positioning done here) when inline.
        {...(inline ? {} : { anchor: 'bottom start' as const })}
        className={classNames(
          'w-[220px] bg-white rounded-lg shadow-lg border border-grey-900 py-2 z-50',
          inline && 'absolute left-0 top-full mt-1'
        )}
      >
        <div className="px-3 pb-1.5">
          <p className="text-[10px] font-semibold uppercase tracking-wider text-primary-t-200">
            {currentConfig.siteName}{' '}
            <span className="font-normal normal-case text-grey-400">(current)</span>
          </p>
        </div>
        <MenuItem>
          <Link
            to={primaryPath}
            onClick={onPrimaryClick}
            className="block mx-1.5 px-2.5 py-1.5 rounded-md text-[13px] text-grey-200 data-[focus]:bg-grey-900/50"
          >
            {primaryLabel}
          </Link>
        </MenuItem>
        {allowBrandSwitch && (
          <BrandSwitchAction
            otherBrand={otherBrand}
            targetPath={switchTargetPath}
            surface={surface}
          />
        )}
      </MenuItems>
    </Menu>
  );
};
